import { Heading, Text, Textarea } from '@amzn/meridian';
import { isEmpty } from 'es-toolkit/compat';
import type { MultiCreativeCampaignPlacementEditorProps } from 'features/traffic/campaigns/MultiCreativeCampaignPlacements/types/multi-creative-campaign-placements-types';
import { type FocusEvent, useCallback, useMemo, useState } from 'react';
import { Field, useField } from 'react-final-form';
import { FeaturedBrandsKeywordsCSS as css } from '../styles/styles';

export const FeaturedBrandsKeywords = ({ path }: MultiCreativeCampaignPlacementEditorProps) => {
  const keywordsPath = `${path}.eligibilityCriteria.keywords`;

  const validateKeywords = useCallback((keywords?: string[]) => {
    if (!keywords || !Array.isArray(keywords) || !keywords.length || keywords.every((keyword) => keyword.trim() === '')) return 'Required';
    return;
  }, []);
  const keywordsField = useField(keywordsPath, {
    validate: validateKeywords,
  });

  const errorMessage = useMemo(
    () => (keywordsField.meta.error ? keywordsField.meta.error : undefined),
    [
      keywordsField.meta.error,
    ],
  );

  const [removedKeywords, setRemovedKeywords] = useState<string[]>([]);
  const nonEmptyRemovedKeywords = useMemo(
    () => removedKeywords.filter((removedKeyword) => !isEmpty(removedKeyword)),
    [
      removedKeywords,
    ],
  );
  const autoCorrectKeywords = useCallback((keywords: string[]) => {
    // Initialize an array to hold the removed keywords.
    let removedKeywords: string[] = [];

    // Filter out keywords that contain commas or are empty, and track removed ones.
    const filteredKeywords = keywords.filter((keyword) => {
      const trimmedKeyword = keyword.trim();
      if (isEmpty(trimmedKeyword) || trimmedKeyword.includes(',')) {
        removedKeywords.push(trimmedKeyword);
        return false;
      }
      return true;
    });

    // Remove duplicates and keep track of them in the removedKeywords array.
    const keywordSet = new Set<string>();
    const uniqueKeywords = filteredKeywords.filter((keyword) => {
      if (keywordSet.has(keyword)) {
        removedKeywords.push(keyword);
        return false;
      }
      keywordSet.add(keyword);
      return true;
    });

    // Limit the number of keywords to 1000 and track the removed ones.
    const correctedKeywords = uniqueKeywords.slice(0, 1000);
    removedKeywords = removedKeywords.concat(uniqueKeywords.slice(1000));
    return {
      correctedKeywords,
      removedKeywords,
    };
  }, []);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (keywordsField.input.value && Array.isArray(keywordsField.input.value) && keywordsField.input.value.length) {
        const { correctedKeywords, removedKeywords } = autoCorrectKeywords(keywordsField.input.value);
        if (!isEmpty(removedKeywords)) {
          keywordsField.input.onChange(correctedKeywords);
          setRemovedKeywords(removedKeywords);
        }
      }
      keywordsField.input.onBlur(event);
    },
    [
      autoCorrectKeywords,
      keywordsField.input,
    ],
  );
  return (
    <>
      <Heading className="my-3" level={5}>
        Targeting
      </Heading>
      <Field
        name={keywordsPath}
        render={({ input }) => (
          <div className={css.keywordsTextarea}>
            <Textarea
              error={!!errorMessage}
              errorMessage={errorMessage}
              helperText="Enter up to 1000 search keywords (separated by line-break) to target. You can copy and paste from the generated CSV, and/or add your own keywords."
              label="Keywords *"
              onBlur={handleBlur}
              onChange={(e) => {
                input.onChange(e.split('\n'));
              }}
              placeholder="Enter keywords"
              resize="none"
              rows={10}
              value={input.value ? input.value.join('\n') : ''}
              width="60%"
            />
          </div>
        )}
      />
      {nonEmptyRemovedKeywords.length > 0 && (
        <Text className="removedKeywords" color="error">
          <span>The following lines have errors and won’t be included when publishing:</span>
          <ul>
            {nonEmptyRemovedKeywords.map((removedKeyword, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
              <li key={index}>
                <strong>{removedKeyword}</strong>
              </li>
            ))}
          </ul>
        </Text>
      )}
    </>
  );
};
